import { KustomerSatisfactionComponent } from 'api/types';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import smileBad from 'assets/icons/csat-icons/smile-bad.svg';
import smileExcellent from 'assets/icons/csat-icons/smile-excellent.svg';
import smileGood from 'assets/icons/csat-icons/smile-good.svg';
import smileOkay from 'assets/icons/csat-icons/smile-okay.svg';
import smileTerrible from 'assets/icons/csat-icons/smile-terrible.svg';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import { RatingButton, StyleCardImg } from 'components/Csat/CsatSurveyContent';
import React from 'react';
import {
  applyKustomerCoreFunctionWithCallback,
  KustomerOnSatisfactionReceived,
  KustomerQuestion,
  KustomerSubmitSatisfactionForm,
} from 'embed/kustomerCore';
import {
  ListOption,
  TextField,
} from '@forethought-technologies/forethought-elements';
import ListDropdown from 'components/ListDropdown/ListDropdown';
import { useAppDispatch, useAppSelector } from 'hooks';
import { addWidgetComponents, selectConversationId } from 'reducers/workflows';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

interface KustomerSatisfactionFormProps {
  isLastStep: boolean;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  /** Components to display inside the form */
  widgetComponent: KustomerSatisfactionComponent;
}

const icons = [smileTerrible, smileBad, smileOkay, smileGood, smileExcellent];

function getSmileIcons(numIcons: number) {
  switch (numIcons) {
    case 2:
      return [smileBad, smileGood];
    case 3:
      return [smileBad, smileOkay, smileGood];
    default:
      return icons;
  }
}

export const KustomerSatisfactionForm = ({
  isLastStep,
  setStepSent,
  stepIndex,
  widgetComponent,
}: KustomerSatisfactionFormProps) => {
  const dispatch = useAppDispatch();
  const conversationId = useAppSelector(selectConversationId);
  const [selectedRating, setSelectedRating] = React.useState(-1);
  const [additionalQuestions, setAdditionalQuestions] =
    React.useState<KustomerOnSatisfactionReceived>();
  const [response, setResponse] =
    React.useState<KustomerSubmitSatisfactionForm>();

  React.useEffect(() => {
    if (additionalQuestions) {
      setResponse({
        ...additionalQuestions?.response,
        satisfactionId: widgetComponent.component_fields.satisfactionId,
      });
    }
  }, [additionalQuestions, widgetComponent.component_fields.satisfactionId]);

  function onSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (response && response.answers) {
      applyKustomerCoreFunctionWithCallback('submitSatisfactionForm', {
        ...response,
        answers: (response?.answers || []).map(answer => {
          if (Array.isArray(answer.answer)) {
            return {
              ...answer,
              answer: answer.answer.join(', '),
            };
          } else {
            return answer;
          }
        }),
        submittedAt: new Date().toISOString(),
      })
        .then(res => {
          if (res) {
            // add event message
            dispatch(
              addWidgetComponents([
                {
                  component_fields: {
                    message: !!widgetComponent.isSent
                      ? 'Updated feedback'
                      : 'Submitted feedback',
                  },
                  component_type: 'event_message',
                },
              ]),
            );
            setStepSent();
          }
        })
        .catch((err: unknown) => {
          // add event message
          dispatch(
            addWidgetComponents([
              {
                component_fields: {
                  message: 'Failed to submit feedback',
                },
                component_type: 'event_message',
              },
            ]),
          );
          sendErrorToSentry({
            additionalContext: { response },
            consoleError: true,
            conversationId: conversationId,
            error: `Error submitting satisfaction form -- ${err}`,
          });
        });
    }
  }

  function onRatingClick(e: React.MouseEvent, index: number) {
    e.preventDefault();
    setSelectedRating(index);
    applyKustomerCoreFunctionWithCallback('submitSatisfactionForm', {
      answers: [],
      rating: index + 1,
      satisfactionId: widgetComponent.component_fields.satisfactionId,
      submittedAt: new Date().toISOString(),
    })
      .then(res => {
        if (res) {
          setAdditionalQuestions(res);
        }
      })
      .catch((err: unknown) => {
        // add event message
        dispatch(
          addWidgetComponents([
            {
              component_fields: {
                message: 'Failed to submit rating',
              },
              component_type: 'event_message',
            },
          ]),
        );
        sendErrorToSentry({
          additionalContext: { response },
          consoleError: true,
          conversationId: conversationId,
          error: `Error submitting satisfaction rating -- ${err}`,
        });
      });
  }

  function getFormInput(question: KustomerQuestion) {
    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
      if (response) {
        setResponse({
          ...response,
          answers: [
            ...(response?.answers || []).filter(
              answer => answer.id !== question.id,
            ),
            {
              answer: e.target.value,
              id: question.id,
              prompt: question.prompt,
            },
          ],
        });
      }
    }

    function listOnChange(e: SelectChangeEvent) {
      if (response) {
        setResponse({
          ...response,
          answers: [
            ...(response?.answers || []).filter(
              answer => answer.id !== question.id,
            ),
            {
              answer: e.target.value,
              id: question.id,
              prompt: question.prompt,
            },
          ],
        });
      }
    }

    const value = response?.answers?.find(
      answer => answer.id === question.id,
    )?.answer;
    const options: ListOption[] = question.enumeration.map(option => ({
      label: option,
      value: option,
    }));

    switch (question.displayType) {
      case 'text':
        return (
          <TextField
            dir='auto'
            label={question.prompt}
            multiline
            onChange={onChange}
            rows={2}
            value={value || ''}
          />
        );
      case 'checkbox':
        return (
          <ListDropdown
            error=''
            id={question.id}
            label={question.prompt}
            multiple
            onChange={listOnChange}
            options={options}
            placeholder=''
            requiredLabel=''
            // @ts-expect-error ListDropdown shenanigans
            value={
              (Array.isArray(value) ? value : value ? [value] : []) as string[]
            }
          />
        );
      case 'radio':
        return (
          <ListDropdown
            error=''
            id={question.id}
            label={question.prompt}
            onChange={listOnChange}
            options={options}
            placeholder=''
            requiredLabel=''
            value={value || ''}
          />
        );
      default:
        return null;
    }
  }

  return (
    <FormWrapper
      className='js-form-step'
      disableSubmitOnEnter={true}
      formTitle={widgetComponent.component_fields.form.introduction}
      isDisabled={!additionalQuestions}
      isLastStep={isLastStep}
      onSubmit={onSubmit}
      stepIndex={stepIndex}
      submitButtonText={!!widgetComponent.isSent ? 'Update' : 'Submit'}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          justifyContent: 'center',
        }}
      >
        {/* SCALE */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box dir='auto' sx={{ textAlign: 'center' }}>
            <Typography variant='font14Bold'>
              {widgetComponent.component_fields.form.ratingPrompt}
            </Typography>
          </Box>
          {/* EMOJIS */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                justifyContent: 'space-between',
              }}
            >
              {getSmileIcons(
                widgetComponent.component_fields.form.scale.options,
              ).map((icon, index) => {
                return (
                  <RatingButton
                    key={index}
                    onClick={e => {
                      onRatingClick(e, index);
                    }}
                    selected={selectedRating === index}
                  >
                    <StyleCardImg alt='rating' src={icon} />
                  </RatingButton>
                );
              })}
            </Box>
            <Box
              dir='auto'
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingTop: '8px',
              }}
            >
              <Typography variant='font12'>
                {widgetComponent.component_fields.form.scale.labelLow}
              </Typography>
              <Typography variant='font12'>
                {widgetComponent.component_fields.form.scale.labelHigh}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* ADDITIONAL QUESTIONS */}
        {additionalQuestions?.form.questions.map(question => {
          return (
            <Box key={question.id}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                {getFormInput(question)}
              </Box>
            </Box>
          );
        })}
        {/* EMPTY BOX FOR SPACE BEFORE SUBMIT */}
        <Box />
      </Box>
    </FormWrapper>
  );
};
